import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useFilters from '@hook/shared/use-filters'

import { parseParametrizationSettingsData } from '@helper/grid'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader,
  Pagination
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IParams,
  IParamsEdit,
  ISettingsContext,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsDate,
  IUpdateParamsEdit
} from '@base/@types/providers/Parametrization/SettingsProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationSettingsHeader from '@base/variables/headers/parametrization/settings'

const SettingsProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(false)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Configuracoes abastecimento')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error, setError] = useState<string | undefined>()
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [data, updateData] = useImmer<any[]>([])
  const [selected, setSelectedOne] = useState(0)

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit, updateCenters] = useImmer({
    centers: [],
    products: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    abastercer: '1',
    idPerfil: null,
    tipoSuprimento: '1',
    politicaEstoque: '1',
    incluirPrevisaoLeadTime: '1',
    maximo: '1',
    frequenciaDePedidos: '1',
    leadTime: '1',
    tempoDeEspera: '1',
    loteMultiplo: '1',
    politicaDeEstoqueArbitraria: '1',
    loteMinimoCD: '1',
    estoqueArbitrado: '1',
    cdDeOrigem: '1',
    status: '1',
    clusterCentro: '1',
    clusterProduto: '1',
    estoqueMinimo: '1',
    utilizarEstoqueMinimo: '1',
    pontoPedido: '1'
  })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleUpdateEditParams = ({ name, value }: IUpdateParamsEdit) => {
    updateEditParams(draft => {
      if (name === 'status') {
        draft.status = value
      } else if (name === 'abastercer') {
        draft.abastercer = value
      } else if (name === 'cdDeOrigem') {
        draft.cdDeOrigem = value
      } else if (name === 'estoqueArbitrado') {
        draft.estoqueArbitrado = value
      } else if (name === 'frequenciaDePedidos') {
        draft.frequenciaDePedidos = value
      } else if (name === 'incluirPrevisaoLeadTime') {
        draft.incluirPrevisaoLeadTime = value
      } else if (name === 'leadTime') {
        draft.leadTime = value
      } else if (name === 'loteMinimoCD') {
        draft.loteMinimoCD = value
      } else if (name === 'loteMultiplo') {
        draft.loteMultiplo = value
      } else if (name === 'maximo') {
        draft.maximo = value
      } else if (name === 'politicaDeEstoqueArbitraria') {
        draft.politicaDeEstoqueArbitraria = value
      } else if (name === 'politicaEstoque') {
        draft.politicaEstoque = value
      } else if (name === 'tipoSuprimento') {
        draft.tipoSuprimento = value
      } else if (name === 'tempoDeEspera') {
        draft.tempoDeEspera = value
      } else if (name === 'clusterProduto') {
        draft.clusterProduto = value
      } else if (name === 'clusterCentro') {
        draft.clusterCentro = value
      } else if (name === 'utilizarEstoqueMinimo') {
        draft.utilizarEstoqueMinimo = value
      } else if (name === 'estoqueMinimo') {
        draft.estoqueMinimo = value
      } else if (name === 'pontoPedido') {
        draft.pontoPedido = value
      } else if (name === 'idPerfil') {
        draft.idPerfil = value
      }
    })
  }

  const getCenters = useCallback(async () => {
    setLoadingEdit(true)
    try {
      const { data: centers } = await parametrization.getCenters()
      const { data: products } = await parametrization.getProducts()

      updateCenters(draft => {
        return Object.assign(draft, {
          centers: parseCommonFilters(centers.data),
          products: parseCommonFilters(products.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoadingEdit(false)
  }, [updateCenters])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    produtoSemFornecedor: false
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationSettingsHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationSettingsHeader')) {
      encrypteHeaderStorange('parametrizationSettingsHeader', headers)
    } else {
      localStorage.removeItem('parametrizationSettingsHeader')
      encrypteHeaderStorange('parametrizationSettingsHeader', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {
    const configuracaoGrid = {
      id: headers[0].columns[0] ? headers[0].columns[0].show : true,
      empresaId: headers[1].columns[0] ? headers[1].columns[0].show : true,
      empresaNome: headers[1].columns[1] ? headers[1].columns[1].show : true,
      produtoNovoId: headers[2].columns[0] ? headers[2].columns[0].show : true,
      produtoNovoNome: headers[2].columns[1]
        ? headers[2].columns[1].show
        : true,
      produtoSimilarId: headers[3].columns[0]
        ? headers[3].columns[0].show
        : true,
      produtoSimilarNome: headers[3].columns[1]
        ? headers[3].columns[1].show
        : true,
      percentualDemandaAbsorvida: headers[4].columns[0]
        ? headers[4].columns[0].show
        : true
    }
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSend = {
      configuracaoGrid
    }
    try {
      const { data: res } = await parametrization.postExportSimilarProduct(
        paramsToSend
      )
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      Swal.close()
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Erro Requisição.',
        description: message,
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }, [toast, filename, headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationSettingsHeader')) {
      const newHeader = decrypterHeaderStorange('parametrizationSettingsHeader')

      // Verifica se há novas colunas comparando com o header padrão
      const currentColumnCount = parametrizationSettingsHeader[2].columns.length
      const storedColumnCount = newHeader[2]?.columns?.length || 0

      // Se houver diferença no número de colunas, força atualização
      if (currentColumnCount !== storedColumnCount) {
        localStorage.removeItem('parametrizationSettingsHeader')
        encrypteHeaderStorange(
          'parametrizationSettingsHeader',
          parametrizationSettingsHeader
        )
        updateHeaders(parametrizationSettingsHeader)
      } else {
        newHeader[0].columns[0] = {
          id: newHeader[0].columns[0].id,
          name: newHeader[0].columns[0].name,
          show: newHeader[0].columns[0].show
        }
        updateHeaders(newHeader)
      }
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      if (name === 'curvaIgualZero') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'produtoSemFornecedor') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
    })
  }

  const handlePageChange = useCallback(async (_page: number) => {}, [])

  const getData = useCallback(async () => {
    setLoading(true)
    // await getMaintence()

    encrypteHeaderStorange('parametrizationSettingsHeader', headers)

    handleStorange()

    try {
      parametrization
        .getSettingsParams()
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })

          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    } catch {
      setLoading(false)
    }
  }, [headers, handleStorange, updateData, updatePagination])

  const updateProductSimilar = useCallback(async () => {
    // try {
    //   const { data: _res } = await parametrization.putProductSimilar({
    //     params: {
    //       id: Number(selected),
    //       empresaId: paramsEdit.empresa.value,
    //       produtoNovoId: paramsEdit.produtoNovo.value,
    //       produtoSimilarId: paramsEdit.produtoSimilar.value,
    //       demandaAbsorvida: Number(paramsEdit.demanda)
    //     }
    //   })
    //   toast({
    //     title: 'Sucesso.',
    //     description: 'Faturamento Minimo editado com Sucesso',
    //     status: 'success',
    //     duration: 2000,
    //     isClosable: true
    //   })
    // } catch (error: any) {
    //   const { data } = error.response
    //   const message =
    //     error.response?.status === 400
    //       ? error.response?.data?.message
    //       : 'Ocorreu um problema interno. Tente novamente mais tarde'
    //   toast({
    //     title: 'Atenção.',
    //     description: message,
    //     status: 'warning',
    //     duration: 3000,
    //     isClosable: true
    //   })
    // }
    setLoading(false)
    setSelectedOne(0)
    getData()
  }, [getData, setLoading, setSelectedOne])

  const deleteData = useCallback(async () => {
    try {
      await parametrization.deleteProductSimilar({
        idMinimumBilling: Number(selected)
      })

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    getData()
  }, [selected, getData, toast, setLoading, setSelectedOne])

  const createData = useCallback(async () => {
    const idsParametroAbastecimento = [
      Number(paramsEdit.abastercer) === 1 ? 1 : null,

      Number(paramsEdit.tipoSuprimento) === 1 ? 2 : null,

      Number(paramsEdit.politicaEstoque) === 1 ? 3 : null,
      Number(paramsEdit.incluirPrevisaoLeadTime) === 1 ? 4 : null,
      Number(paramsEdit.utilizarEstoqueMinimo) === 1 ? 5 : null,

      Number(paramsEdit.maximo) === 1 ? 6 : null,
      Number(paramsEdit.frequenciaDePedidos) === 1 ? 7 : null,
      Number(paramsEdit.leadTime) === 1 ? 8 : null,
      Number(paramsEdit.loteMultiplo) === 1 ? 9 : null,
      Number(paramsEdit.politicaDeEstoqueArbitraria) === 1 ? 10 : null,
      Number(paramsEdit.loteMinimoCD) === 1 ? 11 : null,
      Number(paramsEdit.estoqueArbitrado) === 1 ? 12 : null,
      Number(paramsEdit.estoqueMinimo) === 1 ? 13 : null,

      Number(paramsEdit.cdDeOrigem) === 1 ? 14 : null,
      Number(paramsEdit.status) === 1 ? 15 : null,
      Number(paramsEdit.clusterProduto) === 1 ? 16 : null,
      Number(paramsEdit.clusterCentro) === 1 ? 17 : null,
      Number(paramsEdit.pontoPedido) === 1 ? 18 : null
    ]

    const objPost = {
      idPerfil: paramsHelper.parseUnic(paramsEdit.idPerfil),
      idsParametroAbastecimento: idsParametroAbastecimento.filter(
        item => item !== null
      )
    }
    // eslint-disable-next-line no-debugger
    debugger
    console.log(objPost)

    try {
      await parametrization.postSettingsParams(objPost)
      toast({
        title: 'Sucesso.',
        description: 'Configuracoes de parametros cadastrado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
    }
    setLoading(false)
    setSelectedOne(0)
    getData()
  }, [paramsEdit, getData, toast, setSelectedOne, setLoading])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return []
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return []
  }, [])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'empresa',
          value: {
            label: idData[0].empresa
              ? idData[0].idEmpresa + ' - ' + idData[0].empresa.descricao
              : '',
            value: idData[0].idEmpresa
          }
        })
        handleUpdateEditParams({
          name: 'produtoNovo',
          value: {
            label: idData[0].produto
              ? idData[0].produto.id + ' - ' + idData[0].produto.descricao
              : '',
            value: idData[0].produto.id
          }
        })

        handleUpdateEditParams({
          name: 'produtoSimilar',
          value: {
            label: idData[0].similar
              ? idData[0].similar.id + ' - ' + idData[0].similar.descricao
              : '',
            value: idData[0].similar.id
          }
        })
        handleUpdateEditParams({
          name: 'demanda',
          value: Number(idData[0].porcentagemDemanda)
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selected

      const objFilterData = data.filter(
        item => item.idPerfil === index['id-id']
      )
      const objFilterProfile = profiles.filter(
        item => item.value === index['id-id']
      )

      setSelectedOne(prev => (prev !== similarProduct ? similarProduct : 0))
      if (newSelected === similarProduct) {
        updateEditParams({
          abastercer: '1',
          idPerfil: null,
          tipoSuprimento: '1',
          politicaEstoque: '1',
          incluirPrevisaoLeadTime: '1',
          maximo: '1',
          frequenciaDePedidos: '1',
          leadTime: '1',
          tempoDeEspera: '1',
          loteMultiplo: '1',
          politicaDeEstoqueArbitraria: '1',
          loteMinimoCD: '1',
          estoqueArbitrado: '1',
          cdDeOrigem: '1',
          status: '1',
          clusterCentro: '1',
          clusterProduto: '1',
          estoqueMinimo: '1',
          utilizarEstoqueMinimo: '1',
          pontoPedido: '1'
        })
      } else {
        updateEditParams({
          abastercer: objFilterData[0].parametros.includes(1) ? '1' : '2',
          idPerfil: objFilterProfile[0],
          tipoSuprimento: objFilterData[0].parametros.includes(2) ? '1' : '2',
          politicaEstoque: objFilterData[0].parametros.includes(3) ? '1' : '2',
          incluirPrevisaoLeadTime: objFilterData[0].parametros.includes(4)
            ? '1'
            : '2',
          utilizarEstoqueMinimo: objFilterData[0].parametros.includes(5)
            ? '1'
            : '2',
          maximo: objFilterData[0].parametros.includes(6) ? '1' : '2',
          frequenciaDePedidos: objFilterData[0].parametros.includes(7)
            ? '1'
            : '2',
          leadTime: objFilterData[0].parametros.includes(8) ? '1' : '2',
          tempoDeEspera: objFilterData[0].parametros.includes(19) ? '1' : '2',
          loteMultiplo: objFilterData[0].parametros.includes(9) ? '1' : '2',
          politicaDeEstoqueArbitraria: objFilterData[0].parametros.includes(10)
            ? '1'
            : '2',
          loteMinimoCD: objFilterData[0].parametros.includes(11) ? '1' : '2',
          estoqueArbitrado: objFilterData[0].parametros.includes(12)
            ? '1'
            : '2',
          cdDeOrigem: objFilterData[0].parametros.includes(14) ? '1' : '2',
          status: objFilterData[0].parametros.includes(15) ? '1' : '2',
          clusterCentro: objFilterData[0].parametros.includes(16) ? '1' : '2',
          clusterProduto: objFilterData[0].parametros.includes(17) ? '1' : '2',
          estoqueMinimo: objFilterData[0].parametros.includes(13) ? '1' : '2',
          pontoPedido: objFilterData[0].parametros.includes(18) ? '1' : '2'
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, data, updateEditParams, handleEditParams]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selected,
        isMulti: false
      }
    ]
  }, [selected, handleSelect])

  const handleUpdateYesModal = useCallback(async () => {
    updateEditParams({
      abastercer: '1',
      idPerfil: paramsEdit.idPerfil,
      tipoSuprimento: '1',
      politicaEstoque: '1',
      incluirPrevisaoLeadTime: '1',
      maximo: '1',
      frequenciaDePedidos: '1',
      leadTime: '1',
      tempoDeEspera: '1',
      loteMultiplo: '1',
      politicaDeEstoqueArbitraria: '1',
      loteMinimoCD: '1',
      estoqueArbitrado: '1',
      cdDeOrigem: '1',
      status: '1',
      clusterCentro: '1',
      clusterProduto: '1',
      estoqueMinimo: '1',
      utilizarEstoqueMinimo: '1',
      pontoPedido: '1'
    })
  }, [paramsEdit.idPerfil, updateEditParams])

  const handleUpdateNotModal = useCallback(async () => {
    updateEditParams({
      abastercer: '2',
      idPerfil: paramsEdit.idPerfil,
      tipoSuprimento: '2',
      politicaEstoque: '2',
      incluirPrevisaoLeadTime: '2',
      maximo: '2',
      frequenciaDePedidos: '2',
      leadTime: '2',
      tempoDeEspera: '2',
      loteMultiplo: '2',
      politicaDeEstoqueArbitraria: '2',
      loteMinimoCD: '2',
      estoqueArbitrado: '2',
      cdDeOrigem: '2',
      status: '2',
      clusterCentro: '2',
      clusterProduto: '2',
      estoqueMinimo: '2',
      utilizarEstoqueMinimo: '2',
      pontoPedido: '2'
    })
  }, [paramsEdit.idPerfil, updateEditParams])
  const { profiles } = useFilters()

  useEffect(() => {
    // getFilters({
    //   profiles: true
    // })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SettingsContext.Provider
      value={{
        loading,
        loadingEdit,
        data: parseParametrizationSettingsData(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        query,
        filters,
        hiddenColumns,
        handlePageChange,
        pagination,
        fastQuery,
        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        getCenters,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        createData,
        handleUpdateEditParams,
        handleToogleTable,
        updateProductSimilar,
        deleteData,
        setFilters,
        setFilename,
        handleUpdateNotModal,
        handleUpdateYesModal
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

export default SettingsProvider
export const SettingsContext = createContext<ISettingsContext>(
  {} as ISettingsContext
)
